import axios from "axios";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import { Button, ButtonGroup, Card, CardBody, Col, Input, Row, Table } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, filterHelpers } from "../utils";
import { FilterDateTime, ItemDropdownMenu, ItemTypeahead, ServiceThreadSpinner } from "./";

export default function QCHold() {
   let followDate = new Date();
   followDate = followDate.setDate(followDate.getDate() + 14);
   const cancelToken = axios.CancelToken;
   const source = cancelToken.source();
   const navigate = useNavigate();
   const [comment, setComment] = useState("");
   const [isLoading, setIsLoading] = useState(false);
   const [filters, setFilters] = useState([
      { filterName: "holdFollowUp", value: followDate },
   ]);
   const [screenForm, setScreenForm] = useState({
      payload: {
         itemNumber: null,
         holdReasonId: { label: "--- Select ---", value: -1 },
      },
      holdReasonList: [],
      gridData: [],
   });

   useEffect(() => {
      document.title = `QC Hold Items`;
      let apiCalls = [];
      apiCalls.push(LoadHoldReasons());
      apiCalls.push(LoadGridData());
      Promise.all(apiCalls)
         .then((arrayResults) => {
            let aggResults = {};
            _.each(arrayResults, (x) => Object.assign(aggResults, x));
            if (
               !_.isEmpty(aggResults.holdReasonResult) &&
               !_.isEmpty(aggResults.gridDataResult)
            ) {
               setScreenForm({
                  ...screenForm,
                  holdReasonList: aggResults.holdReasonResult,
                  gridData: aggResults.gridDataResult,
               });
            } else {
               toast.error("an error occured while loading this page");
            }
         })
         .catch(api.catchHandler);

      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const ChangeHeightened = (item) => {
      setIsLoading(true);
      api
         .postS("Quality/ChangeHeightened", { itemNumber: item }, source.token)
         .then()
         .then((res) => {
            if (res.data?.success) {
               toast.success(`Heightened was successfully updated on ${item}`);
               LoadGridData(true);
            } else {
               toast.error(`Failed to update Heightened on ${item}`);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setIsLoading(false);
         });
   };

   const ChangeTightened = (item) => {
      setIsLoading(true);
      api
         .postS("Quality/ChangeTightened", { itemNumber: item }, source.token)
         .then()
         .then((res) => {
            if (res.data?.success) {
               toast.success(`Tightened was successfully updated on ${item}`);
               LoadGridData(true);
            } else {
               toast.error(`Failed to update Tightened on ${item}`);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setIsLoading(false);
         });
   };

   const CreatePayload = () => {
      if (!screenForm.payload.holdReasonId?.value)
         return toast.error("hold reason is missing");
      if (!screenForm.payload.itemNumber?.value)
         return toast.error("item number is missing");
      let payload = {
         holdReasonId: screenForm.payload.holdReasonId?.value,
         itemNumber: screenForm.payload.itemNumber?.value,
      };
      payload["holdComment"] = comment;
      _.map(filters, (filter) => { payload[filter.filterName] = filter.value; });
      payload.holdFollowUp = dateHelpers.formatDateForServer(new Date(payload.holdFollowUp));
      return payload;
   };

   const LoadGridData = (notBundled) => {
      return api
         .fetchS("Quality/LoadQualityHoldGrid", {}, source.token)
         .then((res) => {
            if (notBundled) {
               if (res.data?.success) {
                  setScreenForm({
                     ...screenForm,
                     gridData: res.data.holdGridData,
                     payload: {
                        itemNumber: null,
                        holdReasonId: { label: "--- Select ---", value: -1 }
                     }
                  });
               } else {
                  throw new Error("failed to fetch list");
               }
            } else {
               if (res.data?.success) {
                  return { gridDataResult: res.data.holdGridData };
               } else {
                  throw new Error("failed to fetch list");
               }
            }
         })
         .catch(api.catchHandler);
   };

   const LoadHoldReasons = () => {
      return api
         .fetchS("Quality/LoadHoldReasons", {}, source.token)
         .then((res) => {
            if (res.data?.success) {
               const HOLD_REASONS_LIST = res.data.holdReasonLists;
               return { holdReasonResult: HOLD_REASONS_LIST };
            } else {
               throw new Error("failed to fetch list");
            }
         })
         .catch(api.catchHandler);
   };

   const onChange = (property, value, copy = null) => {
      if (property === "comment") {
         if (value.length > 100) return; //Max length of the comment is 100 characters
         setComment(value);
         return;
      }

      if (copy === null) copy = _.cloneDeep(screenForm);
      _.set(copy, property, value);
      setScreenForm(copy);
      return;
   };

   function onFilterChange(changedFilter) {
      changedFilter.value = moment(changedFilter.value);
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setFilters(filterChanges);
      onChange("payload.holdFollowUp", changedFilter.value)
   }

   const onRemoveHold = (itemWithDesc, itemNumber) => {
      confirmAlert({
         title: "Removing Hold On Item",
         message: `This will remove the hold on ${itemWithDesc}. Do you wish to continue?`,
         buttons: [
            { label: "Yes", onClick: () => RemoveHold(itemNumber), },
            { label: "No", },
         ],
      });
   };

   const PlaceOnHold = () => {
      setIsLoading(true);
      api
         .postS("Quality/PlaceItemOnHold", CreatePayload(), source.token)
         .then((res) => {
            if (res.data?.success) {
               LoadGridData(true);
            } else {
               if (res.data.message) toast.error(res.data.message);
               throw new Error("failed to place item on hold");
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setIsLoading(false);
         });
   };

   const PlaceOnHeightened = () => {
      if (!screenForm.payload.itemNumber?.value)
         return toast.error("item number is missing");

      ChangeHeightened(screenForm.payload.itemNumber?.value);
   };

   const PlaceOnTightened = () => {
      if (!screenForm.payload.itemNumber?.value)
         return toast.error("item number is missing");

      ChangeTightened(screenForm.payload.itemNumber?.value);
   };

   const RemoveHold = (itemNumber) => {
      setIsLoading(true);
      api
         .postS("Quality/RemoveHoldOnItem", { itemNumber: itemNumber }, source.token)
         .then()
         .then((res) => {
            if (res.data?.success) {
               LoadGridData(true);
            } else {
               toast.error("Failed to remove hold on item");
               throw new Error("Failed to remove hold on item");
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setIsLoading(false);
         });
   };

   return (
      <DesktopContainer screenName={"QC Hold"}>
         <div className="filter-header stick-top mb-3">
            <Row>
               <Col xs="12" className="text-center text-white">
                  <h4>Quality Hold Maintenance</h4>
               </Col>
            </Row>
            <hr />
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  <h5 className="pt-1 text-white">Item:</h5>
               </Col>
               <Col className="width400px">
                  <ItemTypeahead
                     value={screenForm.payload.itemNumber}
                     onChange={(opt) => onChange("payload.itemNumber", opt)}
                     includeSampleItems
                     filterItemBy={constants.TYPE_AHEAD_CLASSES.SpecificationItemsWithSamples}
                  />
               </Col>
               <Col>
                  <ButtonGroup>
                     <Button children="SET HEIGHTENED" className="shredButton float-end me-2" onClick={PlaceOnHeightened} />
                     <Button children="SET TIGHTENED" className="shredButton float-end" onClick={PlaceOnTightened} />
                  </ButtonGroup>
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  <h5 className="pt-1 text-white">Hold Reason:</h5>
               </Col>
               <Col xs="6">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     options={screenForm.holdReasonList}
                     value={screenForm.payload.holdReasonId}
                     onChange={(opt) => onChange("payload.holdReasonId", opt)}
                  />
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  <h5 className="pt-1 text-white">Hold Comment:</h5>
               </Col>
               <Col xs="6">
                  <Input
                     type="text"
                     maxLength={100}
                     value={comment}
                     onChange={(e) => onChange("comment", e.target.value)}
                  />
               </Col>
            </Row>
            <Row className="mt-3">
               <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }} >
                  <h5 className="pt-1 text-white">Follow Up:</h5>
               </Col>
               <Col xs="3">
                  <FilterDateTime
                     filterName="holdFollowUp"
                     value={dateHelpers.formatDateForServer(filterHelpers.getValue(filters, "holdFollowUp"))}
                     onChangeCallback={(e) => onFilterChange(e)}
                  />
               </Col>
               <Col xs="3">
                  <Button
                     className="shredButton float-end"
                     style={{ marginRight: "15px" }}
                     onClick={PlaceOnHold}
                     disabled={isLoading}
                  >
                     PLACE ON QC HOLD
                  </Button>
               </Col>
            </Row>
         </div>
         <Card className="dashboard-card">
            <CardBody>
               <ServiceThreadSpinner on={isLoading}>
                  {screenForm.gridData.length !== 0 ? (
                     <Table size="sm" className="data-table">
                        <thead>
                           <tr>
                              <th style={{ width: "9%" }}>Vendor</th>
                              <th style={{ width: "20%" }}>Item</th>
                              <th style={{ width: "9%" }}>Hold Date</th>
                              <th style={{ width: "20%" }}>Reason</th>
                              <th style={{ width: "9%" }}>Follow Up Date</th>
                              <th style={{ width: "18%" }}>Comment</th>
                              <th style={{ width: "5%" }}>HI</th>
                              <th style={{ width: "5%" }}>TI</th>
                              <th style={{ width: "5%" }}>Hold</th>
                           </tr>
                        </thead>
                        <tbody>
                           {_.map(screenForm.gridData, (datum, idx) => {
                              return (
                                 <tr key={idx} className={`tr-${idx % 2 === 0 ? "primary" : "secondary"}`}>
                                    <td
                                       className="data-cell"
                                       style={{ position: "relative" }}
                                       onClick={() =>
                                          navigate(constants.ENDPOINTS.vendorDashboard, {
                                             state: {
                                                label: datum.vendorItemWithName,
                                                value: datum.vendorId,
                                             },
                                             replace: false,
                                          })
                                       }
                                       children={
                                          <span className="shredLink fw-bold noWrap">{datum.vendorId}</span>
                                       }
                                    />
                                    <ItemDropdownMenu
                                       itemNumber={datum.itemNumber}
                                       itemDescription={_.trim(datum.itemWithDesc)}
                                       attachmentCount={datum.attachmentCount}
                                    />
                                    <td className="data-cell">{dateHelpers.toLongDateString(datum.onHoldDate)}</td>
                                    <td className="data-cell" children={datum.holdReason} />
                                    <td className="data-cell">{dateHelpers.toLongDateString(datum.holdFollowUp)}</td>
                                    <td className="data-cell" children={datum.holdComment} />
                                    <td className="data-cell"
                                       children={
                                          <Input
                                             type="checkbox"
                                             checked={datum.heightened}
                                             onChange={() => ChangeHeightened(datum.itemNumber)}
                                          />
                                       }
                                    />
                                    <td className="data-cell"
                                       children={
                                          <Input
                                             type="checkbox"
                                             checked={datum.tightened}
                                             onChange={() => ChangeTightened(datum.itemNumber)}
                                          />
                                       }
                                    />
                                    <td className="data-cell"
                                       children={
                                          <Input
                                             type="checkbox"
                                             checked={datum.onHold}
                                             onChange={() => onRemoveHold(datum.itemWithDesc, datum.itemNumber)}
                                          />
                                       }
                                    />
                                 </tr>
                              );
                           })}
                        </tbody>
                     </Table>
                  ) : (
                     <div className="mt-5" style={{ textAlign: "center" }}>
                        <span>
                           There are currently no items having a QC Hold placed on them
                        </span>
                     </div>
                  )}
               </ServiceThreadSpinner>
            </CardBody>
         </Card>
      </DesktopContainer>
   );
}
